import type { CardSide, CardSideInput } from '../types'
import { findTermSide, isCustomNamedSide, isTermSide } from './cardSides'
import { hasValue, type ValueKind } from './fieldValue'

// Pure field-draft logic for the card-editing surface — `ActiveVocabCard`, which
// is at once the mobile feed's active card and the desktop right panel, and
// (until its retirement) the full-screen editor.
//
// Extracted so it's unit-testable without rendering the component — the
// render-test harness is broken app-wide (see Prompt 4/5/6 notes) — and so the
// paths that must build an identical payload share one implementation instead
// of copies that could drift apart. The two rules at the bottom of this file
// are here for the first reason above all: each of them exists to stop a save
// from destroying content the author wrote, which is exactly the kind of rule
// that should be asserted directly rather than inferred from a rendered card.

// Fixed, stored labels — English regardless of UI locale. This used to be
// stated as "matching every other card-authoring surface in the app", naming
// two that no longer exist (ADR-067 Decision F). There is one authoring
// surface now, so the map has nothing to match: it is simply what gets
// stored. `label` is a cosmetic, backend-stored field and the CSV wire
// format; only the *displayed* eyebrow text is translated.
export const STANDARD_LABEL: Record<string, string> = {
  translation: 'Translation', definition: 'Definition', example: 'Example',
  note: 'Note', image: 'Image', audio: 'Audio', link: 'Link', dialog: 'Dialog',
}
// There is deliberately no default custom label. A Custom field exists to
// carry the name its author typed; substituting a taxonomy word for a name the
// user did not type is the defect this file used to ship. A custom field with
// no name is an incomplete field and is discarded on save, exactly like a field
// with no content (see buildSidesFromDraft).

/**
 * Prefix of a draft key for a field the server has never answered for
 * (ADR-074). The convention was already load-bearing — `addField` has always
 * minted keys this way — and is now named, because two rules depend on telling
 * a local row from a stored one: a local row carries no `serverId`, and its key
 * must never be sent to the server as one.
 */
export const LOCAL_FIELD_KEY_PREFIX = 'new-'

/** A key for a field created on this client, distinguishable from a side id. */
export function newFieldKey(): string {
  return `${LOCAL_FIELD_KEY_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** True for a key minted by `newFieldKey` — i.e. not a stored side's id. */
export function isLocalFieldKey(key: string): boolean {
  return key.startsWith(LOCAL_FIELD_KEY_PREFIX)
}

/** The Term side's stand-in id on a card the server has never answered for.
 * Carries the local prefix so it is covered by the same one predicate as every
 * other identity-less row — a placeholder must never be sent as an identity. */
export const DRAFT_TERM_SIDE_ID = `${LOCAL_FIELD_KEY_PREFIX}term`

/** The id a field occupies in a card-shaped side list: its identity when it has
 * one, its local key when it does not. One expression, because the shape is
 * both what gets sent (where the id must be an identity or absent) and what
 * gets displayed and seeded back (where every row needs some stable id). */
export function fieldSideId(f: Pick<FieldDraft, 'key' | 'serverId'>): string {
  return f.serverId ?? f.key
}

export interface FieldDraft {
  /** React's key for this row, and the handle every callback in the editing
   * surface passes around. Stable for the lifetime of the row: it is chosen
   * once and never reassigned, because reassigning it remounts the row and
   * drops the caret out of a field someone may be typing in. */
  key: string
  /**
   * The id of the row this field IS on the server, or null for one the server
   * has never answered for (ADR-074).
   *
   * This is the field's identity, and it is deliberately separate from `key`.
   * They are equal for a field loaded from a card and they diverge exactly
   * twice: a field created here starts with `serverId: null` and acquires one
   * from the answer to the write that created it, and a field whose stored id
   * changed underneath us (an older backend, which mints fresh ids on every
   * save) adopts the new one. Neither event may disturb `key`, so identity can
   * be corrected without remounting anything.
   */
  serverId: string | null
  kind: string
  label: string
  content: string
  /** True only for a note-kind field the user added as "Custom field" — the
   * one field type with a user-editable label instead of a fixed one. */
  isCustom: boolean
}

/** A card side (definition/example/.../note), as loaded, becomes a draft row.
 * "Custom field" and plain "Note" are both kind='note' — distinguished only
 * by whether the stored label is the fixed "Note" or something else.
 *
 * `serverId` is the side's id, except for a side that came out of
 * `draftToCardSides` for a field the server has never seen: that shape carries
 * the local key in the id slot so the display round trip is lossless, and a
 * local key is not an identity. This is the one place the two directions meet,
 * so it is the one place that has to say so. */
export function sideToDraft(side: CardSide): FieldDraft {
  const kind = side.kind ?? 'note'
  return {
    key: side.id, serverId: isLocalFieldKey(side.id) ? null : side.id,
    kind, label: side.label, content: side.content,
    isCustom: isCustomNamedSide(side),
  }
}

/** Build the full CardSideInput[] payload for a save: Term first (even if
 * empty — callers must check term.trim() before saving at all), then every
 * field with non-blank content, in draft order. A field with blank content
 * is omitted entirely — the backend rejects empty-string side content, and
 * an omitted field is exactly how "remove this field" and "clear it out"
 * both naturally resolve to the same save payload.
 *
 * A custom field whose label is blank is omitted by the same rule: its name is
 * the half of it the user authors, so a nameless custom field is as incomplete
 * as an empty one, and it is discarded rather than saved under an invented
 * name. One rule, two halves — never a substituted label. */
export function buildSidesFromDraft(term: string, fields: FieldDraft[]): CardSideInput[] {
  // A projection of the richer builder below, rather than a second copy of the
  // same three rules. The two must agree by construction: one of them is what
  // gets written and the other is what the reader is looking at while that
  // write is in flight (ADR-072), and a card that displays one side list while
  // sending another would be the display disagreeing with the draft — the one
  // thing that decision exists to make impossible.
  //
  // `id` carries the field's identity to the server (ADR-074): the row this
  // entry IS, so the write keeps it rather than minting a new one. Omitted for
  // a field the server has never answered for — there is nothing to keep — and
  // omitted, not invented, so an older backend that ignores the field behaves
  // exactly as it did before.
  //
  // Only an id that some field actually claims as its identity is sent. A row's
  // key is not evidence of one: the shape below has to give every row an id so
  // it can be displayed and seeded back, and reading that slot as an identity
  // would send a local key to the server as though it were a stored row.
  //
  // The Term is the one side that sends none. Its id lives on the base card
  // rather than in the draft, and nothing needs it: it is matched by kind, it
  // is never reconciled by identity, and the server permits exactly one of it.
  const identities = new Set(fields.map((f) => f.serverId).filter(Boolean))
  return draftToCardSides(term, fields).map(({ id, label, content, kind }) => ({
    ...(identities.has(id) ? { id } : {}),
    label, content, kind: kind ?? undefined,
  }))
}

/**
 * The comparable shape of a side list — what a save would send, with identity
 * removed.
 *
 * Every "does the draft owe a write?" and "has the server's copy changed?"
 * question in the editing surface is asked through this. Identity is excluded
 * on purpose: a field that acquires its stored id from the answer to its own
 * creating write has not changed, and a signature that moved when the id
 * arrived would make the surface believe it owed a write it had just made.
 * Content, name, kind and order are what those questions are actually about.
 */
export function sidesSignature(sides: readonly CardSideInput[]): string {
  return JSON.stringify(sides.map(({ label, content, kind }) => ({ label, content, kind })))
}

/** The signature (above) of what a save of this draft would send. */
export function draftSignature(term: string, fields: FieldDraft[]): string {
  return sidesSignature(buildSidesFromDraft(term, fields))
}

/**
 * The draft as a card's side list — the same content `buildSidesFromDraft`
 * would send, in the shape a card is displayed from.
 *
 * This is how a change reaches the list before its write has been answered
 * (ADR-072 Decision 1): the surface publishes this, and every row renders from
 * it. Nothing here is a second copy of the change — it is the draft, in the
 * shape the reader's side of the app speaks.
 *
 * `base` is the card as the server last answered it, and is consulted for two
 * things only, both of which the draft has no room to carry: the Term side's
 * stored id, and each side's grammatical-form hint (ADR-021). Matching is by
 * the field's identity (`serverId`, ADR-074); a field the server has never
 * answered for has no counterpart and simply has no hint, which is correct —
 * the server has not described it yet.
 *
 * The id slot of a field with no identity carries its local key, so that a
 * draft published through this shape and seeded back out of it (ADR-072) comes
 * back with the same rows it went in with. `sideToDraft` reads a local key back
 * as "no identity", which is what keeps that round trip from inventing one.
 */
export function draftToCardSides(
  term: string, fields: FieldDraft[], base: readonly CardSide[] = [],
): CardSide[] {
  const byId = new Map(base.map((s) => [s.id, s]))
  const baseTerm = findTermSide(base)
  const sides: CardSide[] = [{
    id: baseTerm?.id ?? DRAFT_TERM_SIDE_ID,
    label: 'Term', content: term.trim(), kind: 'term', position: 0,
    form: baseTerm?.form ?? null,
  }]
  for (const f of fields) {
    const content = f.content.trim()
    if (!content) continue
    const label = f.isCustom ? f.label.trim() : STANDARD_LABEL[f.kind]
    if (!label) continue
    sides.push({
      id: fieldSideId(f), label, content, kind: f.kind,
      position: sides.length, form: (f.serverId ? byId.get(f.serverId) : undefined)?.form ?? null,
    })
  }
  return sides
}

/** All non-Term sides of a card, as drafts, in their stored order. */
export function draftsFromCardSides(sides: readonly CardSide[]): FieldDraft[] {
  return sides.filter((s) => !isTermSide(s)).map(sideToDraft)
}

// ── Reconciling a live draft with a later server answer ──────────────────────
//
// The defect this closes is recorded in
// docs/diagnostics/deck-save-visibility-window-2026-08-11.md: the draft was
// seeded once, at mount, from the list query's cache, and nothing re-synced it
// for the lifetime of that mount. Seeded inside a save-visibility window it
// lacked the field the previous write had just added, and because a card write
// is a full replace, the next edit sent the stale list back and deleted that
// field permanently.
//
// The rule below is the answer to "which one wins" — and it is not "the server".
// A draft under a live caret is the author's work, and reseeding it from a
// server answer would be its own content-loss defect, the mirror image of the
// one being fixed. So reconciliation is deliberately ASYMMETRIC:
//
//   Reconciliation may ADD what the server has and the draft does not, and may
//   refresh a field the person has not touched in this mount. It never removes
//   a field, and never rewrites one the person has touched.
//
// That resolution rule is unchanged by ADR-074. What ADR-074 changes is the
// question asked BEFORE it: is this server side the same field as one the draft
// already holds? That used to be answered by guessing — position with a kind
// guard, plus a content fallback — because a full-replace write minted fresh
// side ids and identity did not survive the round trip the merge has to reason
// across. Both halves of the guess fail together on an ordinary sequence: an
// unconfirmed edit makes the content differ by definition, and any shift in the
// field list (a reorder, a field emptied, a field deleted) breaks the position.
// The old rule then "erred towards leaving a duplicate", on the reasoning that a
// duplicate is visible and removable. It is not: the duplicated list is the next
// full-replace payload, so the guess was persisted, and the pre-edit copy of
// every field came back as a second field
// (docs/diagnostics/offline-edit-additive-merge-duplicate-fields-2026-08-12.md).
//
// A field now carries its identity — the id of the row it IS on the server —
// and identity survives the write (ADR-074: `PATCH /cards/{id}` keeps the row a
// payload entry names). So:
//
//   A server side that corresponds to a field the draft already holds updates
//   it or is discarded, under the ownership rule above. It is NEVER appended
//   beside it. Only a side the draft has no counterpart for at all can be
//   added, and a side the draft deliberately dropped is not added back.

/**
 * What the person has changed in this mount. Anything named here is theirs and
 * reconciliation leaves it alone.
 */
export interface DraftTouch {
  /** The Term has been typed into. */
  term: boolean
  /** Keys of side-B fields whose content or name the person has changed. */
  fields: ReadonlySet<string>
  /**
   * Identities (`serverId`) of fields the draft has dropped, so a server answer
   * still carrying one does not resurrect it.
   *
   * Keyed by identity since ADR-074. It used to be keyed by kind and content,
   * which was the only handle available while ids did not survive a write — and
   * which failed on precisely the sequence a person is most likely to perform:
   * fix the text of a field, then decide the field itself should go. The token
   * then described the edited text, the server still held the old text, the
   * deletion went unrecognised, and the field came back carrying content its
   * author had already replaced.
   *
   * Two acts put an identity in here, and a save cannot tell them apart —
   * `buildSidesFromDraft` omits both: a field deleted with the trash can, and a
   * field emptied, which is how "clear it out" and "remove it" have always
   * resolved to one payload (see `droppedFieldIdentities`).
   */
  removed: ReadonlySet<string>
}

/**
 * Identities of fields a save would omit — an emptied field, or a custom field
 * whose name was cleared — among fields the server knows about.
 *
 * The draft keeps such a row for as long as the card is open, so the person can
 * type back into it. The payload does not, and neither does the published draft
 * (ADR-072) that a later mount seeds from. That mount therefore has no row to
 * match the server's copy against, and without this the copy reads as a field
 * the draft has never held and is added back — the emptying undone by the next
 * answer. Emptying a field is a decision exactly as deleting it is; this is
 * what carries that decision across the remount.
 */
export function droppedFieldIdentities(term: string, fields: FieldDraft[]): string[] {
  const kept = new Set(draftToCardSides(term, fields).map((s) => s.id))
  return fields
    .filter((f): f is FieldDraft & { serverId: string } => !!f.serverId && !kept.has(fieldSideId(f)))
    .map((f) => f.serverId)
}

export interface DraftState {
  term: string
  fields: FieldDraft[]
}

/**
 * Merge a server answer into a live draft under the rules above.
 *
 * Returns `null` when the draft already agrees with the server — the no-edit
 * re-entry case, which must stay a no-op so that entering and leaving a card
 * costs nothing and writes nothing.
 *
 * Correspondence is by IDENTITY, in two passes, and only what neither pass can
 * account for is added:
 *
 *  • **By identity.** A server side and a draft field correspond when they are
 *    the same row — `serverId`. This survives everything the old guess did not:
 *    a rewrite, a reorder, both at once, and any number of unconfirmed edits in
 *    between. It is exact, so there is nothing to err on the side of.
 *  • **By kind, in order, for what is left.** Identity can legitimately be
 *    missing: a field created here has none until its own write answers, and a
 *    backend older than ADR-074 mints fresh ids on every save, so a draft's
 *    identities can all go stale at once. What remains on each side is
 *    therefore paired off kind by kind, in order — the k-th unmatched draft
 *    field of a kind to the k-th unmatched server side of it. A pair formed
 *    here also REPAIRS identity: the draft field adopts the server's id, so the
 *    guess is made once and never again. This pass is what keeps the old
 *    behaviour's good half (a draft that has drifted still finds its fields)
 *    without its bad half (a shift in the list turning every field into two).
 *  • **A server side that neither pass accounts for is ADDED** — unless the
 *    draft dropped it deliberately (`touched.removed`). This is the field an
 *    in-flight write created, or Fill, or another device: something the draft
 *    genuinely does not have, which is the only thing an addition may be.
 *
 * Whichever pass forms a correspondence, the ownership rule decides the value
 * and it is unchanged: a field the person has touched keeps their version; one
 * they have not adopts the server's content and name. A correspondence is never
 * resolved by keeping both.
 *
 * Side-B fields the draft has and the server does not are always kept: they are
 * either fields added in this mount that have not been saved yet, or fields the
 * author wrote and this function has no business discarding.
 */
export function reconcileDraftWithServer(
  draft: DraftState, sides: readonly CardSide[], touched: DraftTouch,
): DraftState | null {
  const ordered = [...sides].sort((a, b) => a.position - b.position)
  const serverFields = draftsFromCardSides(ordered)
  const serverTerm = findTermSide(ordered)?.content ?? ''

  let changed = false

  // The Term is one field with one rule: theirs if they have typed into it this
  // mount, the server's otherwise. An empty server Term never overwrites a
  // draft one — that is a card mid-creation, not an answer about its name.
  //
  // The Term is also the one field this function cannot duplicate even in
  // principle: it is not in `serverFields` (filtered by kind), the draft holds
  // it as a single string, and the server refuses a second one outright. Its
  // risk is the opposite one — a stale copy overwriting a live edit — which is
  // what `touched.term` is for.
  let term = draft.term
  if (!touched.term && serverTerm && serverTerm !== draft.term) {
    term = serverTerm
    changed = true
  }

  /** The ownership rule, applied to one correspondence. `key` is never taken
   * from the server: reassigning it remounts the row and drops the caret out of
   * a field the person may be looking at. `serverId` is, and must be — that is
   * the identity this correspondence just established. */
  function resolve(f: FieldDraft, s: FieldDraft): FieldDraft {
    const identity = f.serverId === s.serverId ? f : { ...f, serverId: s.serverId }
    if (identity !== f) changed = true
    if (touched.fields.has(f.key)) return identity
    if (s.content === f.content && s.label === f.label && s.isCustom === f.isCustom) return identity
    changed = true
    return { ...identity, label: s.label, content: s.content, isCustom: s.isCustom }
  }

  // Pass 1 — identity.
  const serverById = new Map<string, FieldDraft>()
  for (const s of serverFields) if (s.serverId) serverById.set(s.serverId, s)
  const accountedFor = new Set<FieldDraft>()
  const next = draft.fields.map((f) => {
    const s = f.serverId ? serverById.get(f.serverId) : undefined
    if (!s) return f
    accountedFor.add(s)
    return resolve(f, s)
  })

  // Pass 2 — kind, in order, over what identity could not pair. Indices into
  // `next` are used so a repaired field is written back in place; the draft's
  // own order is never disturbed by reconciliation.
  const unpairedDraft = new Map<string, number[]>()
  next.forEach((f, i) => {
    if (draft.fields[i].serverId && serverById.has(draft.fields[i].serverId as string)) return
    const slot = unpairedDraft.get(f.kind)
    if (slot) slot.push(i)
    else unpairedDraft.set(f.kind, [i])
  })
  const unaccounted: FieldDraft[] = []
  for (const s of serverFields) {
    if (accountedFor.has(s)) continue
    const slot = unpairedDraft.get(s.kind)
    const i = slot?.shift()
    if (i === undefined) { unaccounted.push(s); continue }
    next[i] = resolve(next[i], s)
  }

  // What is left is genuinely new to the draft — or was deliberately dropped
  // from it, which is a decision reconciliation does not overturn.
  for (const s of unaccounted) {
    if (s.serverId && touched.removed.has(s.serverId)) continue
    next.push(s)
    changed = true
  }

  return changed ? { term, fields: next } : null
}

/** The payload signature of a card as the SERVER currently holds it, in exactly
 * the normalisation a save uses. The editing surface compares against this to
 * decide whether it owes a write, so a draft that reconciliation brought into
 * agreement with the server writes nothing. */
export function serverSideSignature(sides: readonly CardSide[]): string {
  const ordered = [...sides].sort((a, b) => a.position - b.position)
  return draftSignature(
    findTermSide(ordered)?.content ?? '', draftsFromCardSides(ordered),
  )
}

/** The half of a corpus sense that can be written into a card's fields. */
export interface SenseContent {
  definition: string | null
  example: string | null
  translation: string | null
}

/**
 * Whether this card's meaning is already recorded in its own content — the
 * test ADR-063's amendment of 2026-09-11 keys the meaning control's decline on.
 *
 * Keyed on DEFINITION, and on definition alone: it is the field the control
 * anchors to (ADR-066 Decision 1's first field of a kind), the field a chosen
 * sense fills first, and the one whose text amounts to the author having
 * already said which meaning this card is. An Example or a Translation they
 * wrote does not pin a sense the same way, and treating it as if it did would
 * decline on the cards the arrows are genuinely useful for.
 *
 * Repeats count: a second Definition is authored text about this card's
 * meaning exactly as the first one is.
 *
 * Lives beside `fillEmptyFieldsFromSense` and uses the same test (ADR-104 `hasValue`) on
 * purpose. The two rules have to agree about what "written" means — if this one
 * were stricter, a card could be declined for content the fill would have
 * overwritten; if it were looser, the decline would miss a card the fill would
 * have refused to touch, which is the state the amendment exists to close.
 */
export function hasAuthoredDefinition(fields: readonly FieldDraft[]): boolean {
  return hasValue(fields, 'definition')
}

/**
 * Paging to another meaning (the arrows, ADR-063 §4) fills the Definition,
 * Example and Translation the card has NO VALUE for, and nothing else.
 *
 * ADR-104 Decision 6: a kind that already has a value (a non-blank field) is
 * left alone, whatever blank fields it also has. A kind with no value gets
 * only its FIRST field filled; any further blank fields of it stay blank.
 * Filling a blank field in front of the user's own text would displace it as
 * the card's value, which is exactly what Fill is also forbidden to do.
 *
 * The rule is narrow on purpose and is the whole safety property of the
 * feature: switching a sense repoints the card's corpus linkage, and content
 * the author has already written is theirs. A sense that has no value for a
 * kind, or a field that already carries one, both leave the field exactly as
 * it was — so paging back and forth through five meanings can never overwrite
 * a line someone typed, and can never blank a field by landing on a sparse
 * entry. A card whose reader language has no translation row for the
 * candidate behaves as before: `sense.translation` is null, so the field is
 * left untouched.
 *
 * Returns the SAME array reference when nothing changed, so the caller can use
 * identity to decide whether a save is owed at all.
 */
export function fillEmptyFieldsFromSense(
  fields: readonly FieldDraft[], sense: SenseContent,
): FieldDraft[] {
  const fromSense: Record<ValueKind, string | null | undefined> = {
    definition: sense.definition,
    example: sense.example,
    translation: sense.translation,
  }
  // Per kind: the one field to fill — the first field of a kind with no value.
  const targets = new Map<string, string>()
  for (const kind of ['definition', 'example', 'translation'] as const) {
    const content = fromSense[kind]
    if (!content || hasValue(fields, kind)) continue
    const first = fields.find((f) => f.kind === kind)
    if (first) targets.set(first.key, content)
  }
  if (targets.size === 0) return fields as FieldDraft[]
  return fields.map((f) => (targets.has(f.key) ? { ...f, content: targets.get(f.key)! } : f))
}

/**
 * A custom field's name was cleared and the caret has left it.
 *
 * `buildSidesFromDraft` above DISCARDS a custom field whose label is blank, and
 * that is correct at save time — a nameless custom field is as incomplete as an
 * empty one. It is emphatically not correct as a *consequence of retyping a
 * name*: emptying the box in order to write a new name would otherwise commit
 * the field's deletion, taking its content with it, before the new name was
 * typed. Deleting a field is the trash can's job, and that one offers an Undo.
 *
 * So a blank name on blur is restored to what it was when the rename started.
 * `previousLabel` blank means there is nothing to restore to — a state
 * `addField` refuses to create — and the draft is returned untouched rather
 * than given an invented name.
 *
 * Returns the SAME array reference when nothing was restored.
 */
export function restoreBlankCustomLabel(
  fields: readonly FieldDraft[], key: string, previousLabel: string,
): FieldDraft[] {
  const target = fields.find((f) => f.key === key)
  if (!target || target.label.trim()) return fields as FieldDraft[]
  const restored = previousLabel.trim()
  if (!restored) return fields as FieldDraft[]
  return fields.map((f) => (f.key === key ? { ...f, label: restored } : f))
}
